import React from 'react'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import lang from '@/lang'
import styles from './billing-form.css'

import { IbanInput, TextInput, Checkbox, ZipCodeInput, SelectInput, CountryInput } from '@/modules/base/components/inputs/index'
import ModalFooter from '@/modules/base/components/modal/footer/modal-footer'
import Header from '@/modules/base/components/modal/upgrade-modal/header/header'
import CancelButton from '@/modules/base/components/buttons/cancel-button/cancel-button'
import IconCheck from '@assets/images/icons/check.svg'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import { LogoutButton } from '@/modules/base/components/buttons/index'
import { ProductTypes } from '@/modules/base/util/constants'
import ProductMainIcon from '@assets/images/product/main_blue_with_icon.svg'
import ProductDocumentIcon from '@assets/images/product/document_blue_with_icon.svg'
import ProductStaffIcon from '@assets/images/product/staff_blue_with_icon.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ShoppingCart from '@/modules/moduleSubscription/components/list/shopping-cart/shopping-cart'
import HorizontalRuler from '@/modules/base/components/ruler/horizontal-ruler'
import config from '@/config'
import VatNumberInput from '@/modules/customer/components/edit/forms/information/vat-number-input'

const subscriptionTypeIcon = {
  [ProductTypes.DOCUMENT]: ProductDocumentIcon,
  [ProductTypes.MAIN]: ProductMainIcon,
  [ProductTypes.STAFF]: ProductStaffIcon,
}

class BillingForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      data: props.defaultData,
      shoppingCartItems: {},
      period: props.selectedPeriod,
    }

    this.inputChanged = this.inputChanged.bind(this)
    this.handleZipCodeChange = this.handleZipCodeChange.bind(this)
    this.onTitleChanged = this.onTitleChanged.bind(this)
    this.onSalutationChanged = this.onSalutationChanged.bind(this)
    this.onNextClicked = this.onNextClicked.bind(this)
    this.getSalutationSelectProps = this.getSalutationSelectProps.bind(this)
    this.onCheckboxChanged = this.onCheckboxChanged.bind(this)
    this.onIBANValidation = this.onIBANValidation.bind(this)
  }

  validate() {
    const { data } = this.state
    const { validateForm } = this.props
    validateForm && validateForm(data)
  }

  componentDidUpdate(prevProps, prevState) {
    const { defaultData: data, shoppingCartItems, handleChange, selectedPeriod } = this.props
    if (!isEqual(prevProps.defaultData, data)) {
      this.setState({ data })
    }

    if (!isEqual(prevProps.shoppingCartItems, shoppingCartItems)) {
      this.setState({ shoppingCartItems })
    }

    if (!isEqual(prevProps.selectedPeriod, selectedPeriod)) {
      this.setState({ period: selectedPeriod })
    }

    if (prevState.data !== this.state.data) {
      handleChange && handleChange(this.state.data)
    }
  }

  componentDidMount() {
    const { data } = this.state
    if (!data.countryIso) {
      this.setState({ data: { ...data, countryIso: config.defaultCountryAsISO2 } })
    }
  }

  inputChanged(value, name) {
    const { data: currentData } = this.state
    const data = Object.assign({}, currentData, { [name]: value.trim() })
    this.setState({ data }, this.validate)
  }

  handleZipCodeChange({ city, zipCode }) {
    const { data } = this.state
    this.setState({ data: { ...data, city, zipCode } }, this.validate)
  }

  onSalutationChanged(option) {
    const { data } = this.state
    const { name } = option
    this.setState({ data: { ...data, salutation: name } }, this.validate)
  }

  onTitleChanged(value) {
    const { data } = this.state
    this.setState({ data: { ...data, title: value } }, this.validate)
  }

  onNextClicked() {
    const { data, period } = this.state
    const { onNext } = this.props
    onNext({ ...data, period })
  }

  onIBANValidation(isValid) {
    const { onIBANChange } = this.props
    onIBANChange && onIBANChange(isValid)
    this.setState(this.validate)
  }

  handleCountryChange = (option) => {
    this.inputChanged(option?.iso2 || '', 'countryIso')
  }

  onCheckboxChanged(value) {
    const { data } = this.state
    this.setState({ data: { ...data, sepaMandate: value } }, this.validate)
  }

  getSalutationSelectProps() {
    const {
      data: { salutation },
    } = this.state

    return {
      label: lang.customerContactForm.salutation,
      name: 'salutation',
      allowCreate: false,
      notAsync: true,
      customSelectOptions: {
        placeholder: 'Wählen',
        valueKey: 'name',
        labelKey: 'name',
        isClearable: false,
        defaultOptions: true,
      },
      value: salutation ? { name: salutation } : '',
      options: [{ name: 'Herr' }, { name: 'Frau' }],
      handleChange: this.onSalutationChanged,
    }
  }

  render() {
    const {
      trialExpired,
      onCancel,
      onLogout,
      onPrevious,
      isValid,
      confirmLabel,
      subscriptionType,
      disableBackButton,
      onChangeShoppingCart,
      hideFooter,
      classes,
      enableShoppingCart,
      enableItemsFromStorage,
      enablePeriodSelection,
      hideBankDetails,
      customTitle,
      hideHeader = false,
    } = this.props

    const {
      data: {
        bankAccountHolder,
        bankAccountIBAN,
        sepaMandate,
        firstName,
        lastName,
        companyName,
        street,
        zipCode,
        city,
        countryIso,
        vatNumber,
      },
      shoppingCartItems,
      period,
    } = this.state

    const cancelButton = !trialExpired
      ? <CancelButton label={lang.cancelButtonLabel} customClass={styles.cancelButton} onClick={onCancel} />
      : <LogoutButton onClick={onLogout} />

    const header = hideHeader
      ? null
      : (
        <Header
          title={customTitle || lang.billingDataForm.title}
          subTitle={subscriptionType && lang.billingDataForm.subTitle}
          regularSubtitle={this.props.regularSubtitle}
          customClass={classes.header}
        />
      )

    return (
      <div className={styles.wrapper} data-testid="billing-form-wrapper">
        <div className={styles.container}>
          <div className={clsx(styles.leftContainer, !subscriptionType && styles.noPadding)}>
            {subscriptionType && <SVGIcon className={styles.subscriptionTypeIcon} svg={subscriptionTypeIcon[subscriptionType]} height='30px' width='auto' />}
            {header}
            <div className={clsx(styles.content, classes.content)}>
              <div className={styles.invoiceData}>
                {!hideBankDetails
                  && <><div className={styles.row}>
                    <TextInput
                      label={lang.billingDataForm.accountHolderLabel + '*'}
                      name="bankAccountHolder"
                      hideErrorMessage
                      value={bankAccountHolder}
                      onChange={this.inputChanged}
                      required
                    />
                    <IbanInput
                      classNameCreditInstitution={styles.creditInstitutionWrapper}
                      name="bankAccountIBAN"
                      label={lang.billingDataForm.ibanLabel + '*'}
                      value={bankAccountIBAN}
                      onInputChanged={this.inputChanged}
                      handleValidation={this.onIBANValidation}
                      required
                      showCreditInstitution
                    />
                  </div>
                  <div className={styles.row}>
                    <Checkbox
                      name="sepaMandate"
                      customClass={styles.checkbox}
                      onChange={this.onCheckboxChanged}
                      checked={sepaMandate}
                    >
                      <span className={styles.checkboxLabel}>
                        {lang.upgradeSubscriptionSepaMandateLabel}*
                      </span>
                    </Checkbox>
                  </div>
                  <HorizontalRuler className={styles.divider} />
                  </>}
                <div className={styles.row}>
                  <TextInput
                    label={lang.companyNameLabel + '*'}
                    name="companyName"
                    hideErrorMessage
                    value={companyName}
                    onChange={this.inputChanged}
                    required
                  />
                </div>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <div className={styles.salutation}>
                      <SelectInput {...this.getSalutationSelectProps()} />
                    </div>
                    <TextInput
                      label={lang.firstNameLabel + '*'}
                      name="firstName"
                      hideErrorMessage
                      customClass={styles.firstName}
                      value={firstName}
                      onChange={this.inputChanged}
                      required
                    />
                  </div>
                  <TextInput
                    label={lang.lastNameLabel + '*'}
                    name="lastName"
                    hideErrorMessage
                    value={lastName}
                    onChange={this.inputChanged}
                    required
                  />
                </div>
                <div className={styles.row}>
                  <TextInput
                    label={lang.streetFieldLabel + '*'}
                    name="street"
                    hideErrorMessage
                    value={street}
                    onChange={this.inputChanged}
                    required
                  />
                  <div className={styles.column}>
                    <ZipCodeInput
                      zipCode={zipCode}
                      hideErrorMessage
                      zipCodeLabel={lang.zipLabel + '*'}
                      city={city}
                      cityLabel={lang.locationLabel + '*'}
                      onChange={this.handleZipCodeChange}
                      required
                    />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <CountryInput
                      menuPlacement='top'
                      maxMenuHeight={260}
                      value={countryIso}
                      onChange={this.handleCountryChange}
                      includeCodeInLabel
                    />
                  </div>
                  <div className={styles.column}>
                    {countryIso !== config.defaultCountryAsISO2
                      && <VatNumberInput
                        customClass={styles.vatNumberInput}
                        vatNumber={vatNumber}
                        showValidationButton={false}
                        onChange={this.inputChanged}
                        placeholder={lang.missingCompanyData.placeholder}
                      />
                    }
                  </div>
                </div>
              </div>
              {!hideBankDetails && <div className={styles.hint}>{lang.upgradeSubscriptionSepaHint}</div>}
            </div>
          </div>
          {enableShoppingCart
            ? <div className={styles.rightContainer}>
              <ShoppingCart
                key="shopping-cart-checkout"
                items={shoppingCartItems}
                showCheckoutButton={false}
                onChange={onChangeShoppingCart}
                onSelectedPeriodChange={(value) => this.setState({ period: value })}
                selectedPeriod={period}
                enableItemsFromStorage={enableItemsFromStorage}
                enablePeriodSelection={enablePeriodSelection}
              />
            </div>
            : null
          }
        </div>
        {!hideFooter && (
          <ModalFooter customClass={classes.footer} hasBackButton={!disableBackButton} onBackButtonClicked={!disableBackButton && onPrevious}>
            {cancelButton}
            <ButtonIconSquare
              label={confirmLabel}
              icon={IconCheck}
              customClass={styles.continueButton}
              onClick={() => this.onNextClicked(this.state.period)}
              disabled={!isValid}
            />
          </ModalFooter>
        )}
      </div>
    )
  }
}

BillingForm.propTypes = {
  confirmLabel: PropTypes.string,
  trialExpired: PropTypes.bool,
  defaultData: PropTypes.object,
  validateForm: PropTypes.func,
  isValid: PropTypes.bool,
  onCancel: PropTypes.func,
  onIBANChange: PropTypes.func,
  hideFooter: PropTypes.bool,
  classes: PropTypes.object,
  hideBankDetails: PropTypes.bool,
  customTitle: PropTypes.string,
  hideHeader: PropTypes.bool,
}

BillingForm.defaultProps = {
  confirmLabel: lang.continueButtonLabel,
  hideFooter: false,
  hideBankDetails: false,
  defaultData: {},
  classes: {},
}

// HINWEIS: Das Layout ist jetzt durch Media Queries in billing-form.css responsiv.

export default BillingForm
